import CircularProgress from '@material-ui/core/CircularProgress';
import Delete from '@material-ui/icons/Delete';
import { useLocation } from '@reach/router';
import React, { MouseEvent } from 'react';

import {
  useChangeLocation,
  useCurrentLocation,
  useDeleteNotification,
  useLocations,
  useToken,
  useUpdateNotificationReadStatus,
} from 'api';
import permissions from 'common/permissions';
import PermissionsGate from 'components/shared/PermissionsGate';
import { Notification } from 'models';

import NotificationIcon from '../NotificationIcon';
import { iconColor, routeToNotification } from '../notificationUtils';
import TaskApproval from './TaskApproval';

import './NotificationListItem.scss';

interface NotificationListItemProps {
  notification: Notification;
  toggleShowAlert: () => void;
  onReloadNotifications: () => void;
  closeNotificationCenter: () => void;
}

const NotificationListItem: React.FC<NotificationListItemProps> = ({
  notification,
  toggleShowAlert,
  onReloadNotifications,
  closeNotificationCenter,
}) => {
  const locationsQuery = useLocations();
  const rooftopList = locationsQuery?.data ?? [];

  const changeLocationQuery = useChangeLocation();
  const deleteNotificationMutation = useDeleteNotification();
  const updateReadStatusMutation = useUpdateNotificationReadStatus();
  const location = useLocation();

  const handleDeleteNotification = async (e?: MouseEvent) => {
    e?.stopPropagation();
    deleteNotificationMutation.mutate(notification.id);
  };

  const { data: currentUser } = useToken();
  const currentLocation = useCurrentLocation();

  const handleNotificationClick = async () => {
    if (!notification.read) {
      await updateReadStatusMutation.mutateAsync(notification.id);
    }
    closeNotificationCenter();
    if (currentUser !== undefined && currentLocation !== undefined) {
      routeToNotification(
        notification,
        toggleShowAlert,
        currentUser,
        currentLocation,
        location.pathname.split('/')?.slice(1)?.join('/') || 'Dashboard',
        rooftopList,
        changeLocationQuery.mutateAsync
      );
    }
  };

  return (
    <div
      className={`NotificationListItem-notification ${
        deleteNotificationMutation.isLoading ? 'is-deleting' : ''
      }`}
      onClick={handleNotificationClick}
      role="button"
      tabIndex={-1}
    >
      <NotificationIcon type={notification.notificationType} />
      {deleteNotificationMutation.isLoading ? (
        <div className="NotificationListItem-status NotificationListItem-progress">
          <CircularProgress color="primary" size={24} />
        </div>
      ) : (
        <>
          <div className="NotificationListItem-status">
            {!notification.read && (
              <div className="NotificationListItem-status-dot" />
            )}
          </div>
          <PermissionsGate permissions={[permissions.NOTIFICATIONS_DELETE]}>
            <div
              role="button"
              tabIndex={-1}
              onClick={handleDeleteNotification}
              className="NotificationListItem-actions"
            >
              <Delete style={{ color: iconColor }} />
            </div>
          </PermissionsGate>
        </>
      )}
      <div className="NotificationListItem-message">
        {notification.message}
        <TaskApproval
          notification={notification}
          onClick={handleDeleteNotification}
        />
      </div>
    </div>
  );
};

export default NotificationListItem;
